import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import type { PlaceInput as Place } from '@tcrd/shared';
import { Button, Card, cn, Notice, SelectField, TextArea, TextField } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { PlaceInput } from '../../components/PlaceInput';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import type { ServiceDto } from '../../lib/types';
import { DAY_SHORT, type RecurringSeries } from './types';

type PatternType = 'WEEKDAYS' | 'DAILY' | 'WEEKLY_DAYS' | 'CUSTOM_DATES';
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santo_Domingo' }).format(new Date(Date.now() + 86_400_000));

const EXAMPLES = ['Trabajo', 'Escuela', 'Universidad', 'Cita médica', 'Servicio empresarial'];

export function RecurringCreatePage() {
  const history = useHistory();
  const toast = useToast();
  const services = useQuery({ queryKey: ['services'], queryFn: () => api<ServiceDto[]>('/services') });
  const [serviceId, setServiceId] = useState('');
  const [title, setTitle] = useState('Trabajo');
  const [origin, setOrigin] = useState<Place | null>(null);
  const [destination, setDestination] = useState<Place | null>(null);
  const [type, setType] = useState<PatternType>('WEEKDAYS');
  const [days, setDays] = useState<number[]>([1, 3, 5]);
  const [dates, setDates] = useState<string[]>([]);
  const [newDate, setNewDate] = useState(today());
  const [outbound, setOutbound] = useState('07:30');
  const [roundTrip, setRoundTrip] = useState(true);
  const [returnTime, setReturnTime] = useState('17:30');
  const [startDate, setStartDate] = useState(today());
  const [continuous, setContinuous] = useState(true);
  const [endDate, setEndDate] = useState('');
  const [passengers, setPassengers] = useState('1');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!serviceId && services.data?.length) setServiceId((services.data.find((s) => s.code === 'COMMUTE') ?? services.data[0]!).id);
  }, [services.data, serviceId]);

  const submit = async () => {
    if (!origin || !destination) return setError('Indica el origen y el destino');
    setError(null);
    setBusy(true);
    try {
      const res = await api<RecurringSeries>('/recurring-rides', {
        body: {
          serviceTypeId: serviceId,
          title,
          origin,
          destination,
          passengers: Number(passengers) || 1,
          notes: notes || undefined,
          pattern: type === 'WEEKLY_DAYS' ? { type, days } : type === 'CUSTOM_DATES' ? { type, dates } : { type },
          outboundTime: outbound,
          returnTime: roundTrip ? returnTime : undefined,
          startDate,
          endDate: continuous ? undefined : endDate || undefined,
        },
      });
      toast('Solicitud enviada. El conductor te enviará la cotización.', 'success');
      history.replace(`/app/recurring/${res.id}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const chip = (on: boolean) => cn('tap rounded-full border px-4 text-sm', on ? 'border-brand-red bg-brand-red/10 font-semibold' : 'border-[var(--border)]');

  return (
    <Page title="Nuevo viaje recurrente" back="/app/recurring">
      <div className="flex flex-col gap-4">
        <Card className="p-5 flex flex-col gap-3">
          <TextField label="Nombre" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} />
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((x) => <button key={x} className={chip(title === x)} onClick={() => setTitle(x)}>{x}</button>)}
          </div>
          <PlaceInput label="Origen" value={origin} onChange={setOrigin} />
          <PlaceInput label="Destino" value={destination} onChange={setDestination} dotClass="bg-brand-red" allowCurrentLocation={false} />
          <SelectField label="Servicio" value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
            {services.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SelectField>
        </Card>

        <Card className="p-5 flex flex-col gap-3">
          <p className="font-semibold">Frecuencia</p>
          <div className="flex flex-wrap gap-2">
            {([
              ['WEEKDAYS', 'Lunes a viernes'],
              ['DAILY', 'Todos los días'],
              ['WEEKLY_DAYS', 'Días específicos'],
              ['CUSTOM_DATES', 'Fechas personalizadas'],
            ] as const).map(([k, l]) => <button key={k} className={chip(type === k)} onClick={() => setType(k)}>{l}</button>)}
          </div>
          {type === 'WEEKLY_DAYS' && (
            <div className="grid grid-cols-7 gap-1">
              {DAY_SHORT.map((d, i) => (
                <button key={d} aria-pressed={days.includes(i)} onClick={() => setDays((x) => (x.includes(i) ? x.filter((y) => y !== i) : [...x, i]))} className={cn('tap rounded-xl border text-sm', days.includes(i) ? 'bg-brand-black text-white border-brand-black dark:bg-white dark:text-brand-black' : 'border-[var(--border)]')}>{d}</button>
              ))}
            </div>
          )}
          {type === 'CUSTOM_DATES' && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input type="date" value={newDate} min={today()} onChange={(e) => setNewDate(e.target.value)} className="flex-1 h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3" aria-label="Fecha" />
                <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => newDate && setDates((x) => [...new Set([...x, newDate])].sort())}>Agregar</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {dates.map((d) => (
                  <span key={d} className="inline-flex items-center gap-1 rounded-full bg-black/5 dark:bg-white/10 pl-3 pr-1 py-1 text-sm">
                    {d}
                    <button className="size-7 flex items-center justify-center" aria-label={`Quitar ${d}`} onClick={() => setDates((x) => x.filter((y) => y !== d))}><X className="size-4" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Hora de ida" type="time" value={outbound} onChange={(e) => setOutbound(e.target.value)} />
            {roundTrip ? <TextField label="Hora de regreso" type="time" value={returnTime} onChange={(e) => setReturnTime(e.target.value)} /> : <div />}
          </div>
          <label className="tap flex items-center gap-3 text-sm"><input type="checkbox" className="size-5 accent-[#E5252A]" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} /> Ida y regreso</label>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Desde" type="date" min={today()} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            {!continuous && <TextField label="Hasta" type="date" min={startDate} value={endDate} onChange={(e) => setEndDate(e.target.value)} />}
          </div>
          <label className="tap flex items-center gap-3 text-sm"><input type="checkbox" className="size-5 accent-[#E5252A]" checked={continuous} onChange={(e) => setContinuous(e.target.checked)} /> Sin fecha de fin (continua)</label>
          <TextField label="Pasajeros" type="number" min={1} max={20} value={passengers} onChange={(e) => setPassengers(e.target.value)} />
          <TextArea label="Notas para el conductor" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Card>
        {error && <Notice tone="danger">{error}</Notice>}
        <Button variant="accent" size="lg" loading={busy} onClick={submit}>Solicitar cotización</Button>
        <p className="text-xs text-center text-[var(--text-muted)]">El conductor puede cotizar por viaje, por semana, por mes o por paquete. No se realizan cargos automáticos.</p>
      </div>
    </Page>
  );
}
